import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Nav } from './components/Nav';
import { Footer } from './components/Footer';
import { useReveal } from './useReveal';

import '@/styles/marketing/marketing.css';
import '@/styles/marketing/components.css';
import '@/styles/marketing/agent-ledger.css';
import '@/styles/marketing/lead-form.css';
import '@/styles/marketing/product.css';
import '@/styles/marketing/mindflow.css';
import '@/styles/marketing/projectflow.css';
import '@/styles/marketing/kinetix.css';
import '@/styles/marketing/blog.css';
import '@/styles/marketing/legal.css';
import '@/styles/marketing/advanced.css';

/**
 * Shell for all public marketing pages. Owns the `.marketing-root`
 * namespace (light theme), the shared Nav/Footer, smooth scrolling,
 * scroll restoration on navigation, and the reveal-on-scroll observer.
 */
export function MarketingLayout(): React.JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const { pathname, hash } = useLocation();

  useReveal(rootRef, pathname);

  // Smooth scroll only while a marketing page is mounted.
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  // On route change: honour a #hash target if present, else scroll to top.
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div ref={rootRef} className="marketing-root">
      <Nav />
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
